import { BottomSheet, Button, Flag, Typography } from '@s4l/ds'
import { IconCountry } from '@s4l/icons/IconCountry'
import { IconSuccessFilled } from '@s4l/icons/IconSuccessFilled'
import { useS4LContext } from '@s4l/react-context'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { clickCountrySelector, clickMarket } from 'client/contexts/Tracking/events'
import { useRouter } from 'next/router'
import React, { useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { COUNTRIES } from 'src/constants/countries'
import { getSearch } from 'src/effects/Search/selectors'
import { useMarket } from 'src/hooks/useMarket'
import type { MarketType } from 'src/types/market'
import styles from './styles.module.css'
import { buildLocationURL, buildSearchQuery } from './utils'

export const NEW_MARKETS = ['xgAR', 'xgEN', 'sg', 'ec', 'dz']

interface CountrySelectorProps {
  variant?: 'button' | 'dropdown'
  toHomepage?: boolean
}

export function CountrySelector({ variant = 'button', toHomepage = false }: CountrySelectorProps) {
  const { i18n } = useS4LContext()
  const dispatch = useDispatch()
  const marketConfig = useMarket()
  const search = useSelector(getSearch)
  const router = useRouter()

  const { feature_flag: featureFlag } = router.query

  const [selected, setSelected] = useState(marketConfig)
  const [listOpen, setListOpen] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredCountries = useMemo(() => {
    return Object.keys(COUNTRIES)
      .filter(key => key !== 'zz' && (featureFlag === 'new-markets' || !NEW_MARKETS.includes(key)))
      .map(key => COUNTRIES[key])
  }, [featureFlag])

  const toggleList = () => {
    if (!listOpen) {
      dispatch(sendTrackingRequestAction({ event: clickCountrySelector() }))
    }
    setListOpen(!listOpen)
  }

  const isSelected = (country: MarketType) => {
    return country.market === selected.market && country.locale === selected.locale
  }

  const selectItem = (selectedCountry: MarketType) => {
    if (typeof window === 'undefined') return

    setSelected({
      ...selectedCountry,
      minBookingDate: selected.minBookingDate,
    })

    dispatch(sendTrackingRequestAction({ event: clickMarket(selectedCountry.market) }))

    const marketURL = buildLocationURL(selectedCountry, toHomepage)
    const searchParams = buildSearchQuery(search)

    setListOpen(false)

    window.location.href = `${marketURL}&${searchParams}`
  }

  const handleBottomSheetAnimationStart = () => {
    if (typeof window === 'undefined') return

    if (listOpen && dropdownRef.current) {
      const selectedButton = dropdownRef.current.querySelector(
        '[data-selected="true"]'
      ) as HTMLElement

      if (selectedButton) {
        selectedButton.scrollIntoView({ behavior: 'auto', block: 'center' })
      } else {
        dropdownRef.current.scrollTop = dropdownRef.current.scrollHeight
      }
    }
  }

  return (
    <>
      {variant === 'button' && (
        <Button
          data-testid="country-selector-button"
          variant="text-rounded"
          size="small"
          onClick={toggleList}
          onKeyDown={toggleList}
          leadingIcon={<Flag countryCode={selected.countryCode} />}
          aria-label={i18n.formatMessage({ id: 'global.select-country-region' })}
          aria-expanded={listOpen}
        >
          {selected.currency}
        </Button>
      )}
      {variant === 'dropdown' && (
        <button
          type="button"
          className={styles.countrySelectorButton}
          onClick={toggleList}
          onKeyDown={toggleList}
          aria-label={i18n.formatMessage({ id: 'global.country-region' })}
          aria-expanded={listOpen}
        >
          <div>
            <IconCountry />
            <Typography as="p" variant="body" size="l" className="flex-grow-1" ellipsis>
              {i18n.formatMessage({ id: 'global.country-region' })}
            </Typography>
          </div>
          <div>
            <Flag countryCode={selected.countryCode} />
            <Typography variant="body" size="m" bold underline as="p">
              {selected.locale.split('-').at(0)?.toUpperCase()}
              &nbsp;·&nbsp;
              {selected.currency}
            </Typography>
          </div>
        </button>
      )}
      <BottomSheet open={listOpen} onOpenChange={setListOpen}>
        <BottomSheet.Content
          title={i18n.formatMessage({ id: 'global.select-country-region' })}
          size="l"
          modalOnDesktop
          contentPadding={false}
          onAnimationStart={handleBottomSheetAnimationStart}
        >
          <div ref={dropdownRef} className={styles.dropdown} data-testid="country-selector-modal">
            {filteredCountries.map(country => (
              <button
                key={country.locale}
                type="button"
                className={`${styles.option} ${isSelected(country) && styles.optionSelected}`}
                data-testid={country.market}
                data-selected={isSelected(country) ? 'true' : undefined}
                onMouseDown={() => selectItem(country)}
              >
                <div className={styles.optionContent}>
                  <div className={styles.optionLabel}>
                    <Flag countryCode={country.countryCode} />
                    <Typography bold>
                      {`${country.countryName} (${country.countryLanguage})`}
                    </Typography>
                  </div>
                  <Typography>{`${country.currencySymbol} · ${country.currency}`}</Typography>
                </div>
                {isSelected(country) && <IconSuccessFilled className={styles.checkIcon} />}
              </button>
            ))}
          </div>
        </BottomSheet.Content>
      </BottomSheet>
    </>
  )
}
